import React, { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import EnhancedTable from "@/hocComponents/EnhancedTable";
import http from "@/utils/http";
import api from "@/utils/api";
import { buildRequestParams, formatDate } from "./utils";

export default function BookTab({ year, refreshKey, onLoadingChange, journalType }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
  const [entityList, setEntityList] = useState([]);
  const [bankNameList, setBankNameList] = useState([]);
  const requestIdRef = useRef(0);

  const load = async (page, count, nextFilters) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = buildRequestParams(
        {
          page,
          count,
          year,
          journalList: journalType === "CR" ? ["CR"] : ["CP", "RO"],
        },
        nextFilters,
        ["date1"],
      );
      const res = await http.post(api.getShowBookUnreceived, params);
      if (requestId !== requestIdRef.current) return;
      if (res?.code === "1") {
        setList(res.data?.list || []);
        setPagination({
          current: res.data?.pageNum || page,
          pageSize: res.data?.pageSize || count,
          total: res.data?.total || 0,
        });
      } else {
        message.error(res?.msg);
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      message.error("Failed to load data, please try again later");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const loadOptions = async (apiUrl, setter) => {
    try {
      const res = await http.post(apiUrl, { year });
      if (res?.code === "1") {
        setter((res.data || []).map((i) => ({ value: i.value, label: i.label })));
      }
    } catch (e) {
      setter([]);
    }
  };

  useEffect(() => {
    setFilters({});
    load(1, pagination.pageSize, {});
  }, [year, refreshKey]);

  useEffect(() => {
    loadOptions(api.getBankRecByTrust, setEntityList);
    loadOptions(api.getBankRecByBankName, setBankNameList);
  }, [year]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading]);

  const columns = useMemo(
    () => [
      { title: "Amount", dataIndex: "amountRow", filterKey: "amount" },
      { title: "Entity", dataIndex: "trust", filterKey: "trust", filterType: "select", selectOptions: entityList },
      { title: "Bank Name", dataIndex: "bankName", filterKey: "bankName", filterType: "select", selectOptions: bankNameList },
      { title: "Bank Account #", dataIndex: "bankAccount", filterKey: "bankAccount" },
      { title: "PPM Bank #", dataIndex: "bank", filterKey: "bank" },
      { title: "Batch #", dataIndex: "batch", filterKey: "batch" },
      { title: "Date", dataIndex: "date1", filterKey: "date1", filterType: "date", render: formatDate },
      { title: "Jnl", dataIndex: "journal" },
      { title: "User Id", dataIndex: "userId", filterKey: "userId" },
    ],
    [entityList, bankNameList],
  );

  const handleTableChange = (p, tableFilters) => {
    const merged = { ...filters };
    Object.keys(tableFilters || {}).forEach((k) => {
      if (tableFilters[k]?.length > 0) merged[k] = tableFilters[k][0];
    });
    load(p.current || 1, p.pageSize || pagination.pageSize, merged);
  };

  return (
    <EnhancedTable
      columns={columns}
      dataSource={list}
      loading={loading}
      rowKey={(r) => r?.bankTransNewId ?? r?.id}
      filters={filters}
      onFilterChange={(k, v) => setFilters((prev) => ({ ...prev, [k]: v }))}
      onFilterSubmit={(f) => {
        setFilters(f);
        load(1, pagination.pageSize, f);
      }}
      onChange={handleTableChange}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (t) => `Total ${t} items`,
      }}
      scroll={{ y: "calc(100vh - 320px)" }}
    />
  );
}